import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, X } from "lucide-react";
import { searchNews } from "@/lib/api";

/**
 * Debounced search over past capsule segments. Renders an input with
 * inline results (headline + snippet + section chip + relative date).
 * Clicking a result navigates to the parent capsule.
 */
export default function SearchBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      setErr(null);
      return;
    }
    setLoading(true);
    setErr(null);
    timerRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.aborted = true;
        const token = { aborted: false };
        abortRef.current = token;
        const data = await searchNews(q.trim(), 15);
        if (!token.aborted) {
          setResults(data.results || []);
          setOpen(true);
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Search failed. Try again.");
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const clear = () => { setQ(""); setResults([]); setOpen(false); };

  const go = (r) => {
    navigate(`/capsule/${r.capsule_id}`);
    setOpen(false);
  };

  const relDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      const days = Math.floor(diff / 86400);
      if (days < 30) return `${days}d ago`;
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <section className="mt-16">
      <div className="mb-4 flex items-baseline gap-3">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-rose-600">A2</span>
        <h2 className="text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500">
          Search past stories
        </h2>
      </div>

      <div className="relative">
        <div className="flex items-center gap-3 border border-zinc-950 bg-white px-5 py-4">
          <Search className="h-5 w-5 flex-shrink-0 text-zinc-500" />
          <input
            data-testid="search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q.trim().length >= 2 && setOpen(true)}
            placeholder='Search stories, e.g. "AI", "elections", "Bengaluru"'
            className="flex-1 bg-transparent text-base outline-none placeholder:text-zinc-400"
            aria-label="Search past capsule stories"
          />
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
          ) : q ? (
            <button
              type="button"
              onClick={clear}
              data-testid="search-clear-btn"
              aria-label="Clear search"
              className="text-zinc-500 hover:text-zinc-950"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {open && q.trim().length >= 2 && (
          <div
            data-testid="search-results"
            className="mt-3 border border-zinc-200 bg-white"
          >
            {err && (
              <div className="border-b border-zinc-200 px-5 py-4 text-sm text-rose-600">{err}</div>
            )}
            {!err && !loading && results.length === 0 && (
              <div className="px-5 py-6 text-sm text-zinc-500">
                No matches yet. Search runs across every capsule you (and others) have brewed. Try a broader term.
              </div>
            )}
            {results.map((r, i) => (
              <div
                key={`${r.capsule_id}-${i}`}
                role="button"
                tabIndex={0}
                onClick={() => go(r)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    go(r);
                  }
                }}
                data-testid={`search-result-${i}`}
                className="group block w-full cursor-pointer border-b border-zinc-200 px-5 py-4 text-left last:border-b-0 hover:bg-zinc-50 focus:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-600"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-lg font-bold tracking-tight text-zinc-950 group-hover:text-rose-600">
                    {r.headline}
                  </p>
                  <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {r.section && <span className="text-rose-600">{r.section}</span>}
                    <span>{relDate(r.created_at)}</span>
                  </div>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{r.snippet}</p>
                {(r.source_hint || r.source_url) && (
                  <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    {r.source_hint && (
                      <span>Source: {r.source_hint}{r.story_date ? ` · Reported ${r.story_date}` : ""}</span>
                    )}
                    {r.source_url && (
                      <a
                        href={r.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="border-b border-rose-600 text-rose-600 hover:text-zinc-950 hover:border-zinc-950"
                      >
                        View original ↗
                      </a>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
